if (!customElements.get('autocomplete-search')) {
  customElements.define('autocomplete-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this._timer = null;

      this._loadTrending();

      this.input?.addEventListener('input', () => {
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this._onInput(this.input.value.trim()), 500);
      });

    }

    disconnectedCallback() {
      clearTimeout(this._timer);
    }

    get _cfg() {
      return {
        appId:       this.getAttribute('app-id'),
        apiKey:      this.getAttribute('api-key'),
        products:    this.getAttribute('index-name'),
        suggestions: this.getAttribute('suggestions-index'),
        teams:       this.getAttribute('teams-index'),
        trendingLimit: parseInt(this.getAttribute('trending-limit')) || 4,
      };
    }

    _buildOptionalFilters() {
      const favorites = (this.dataset.favoriteTeams || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => s.replace(/^fav_team-/i, ''))
        .map(s => s.replace(/\s+/g, '-').toUpperCase());
      return favorites.map((facet, i) => {
        const score = Math.max(10000, 30000 - i * 5000);
        return `named_tags.team:${facet}<score=${score}>`;
      });
    }

    _optionalFiltersParams(optionalFilters) {
      if (!optionalFilters.length) return '';
      return `&optionalFilters=${encodeURIComponent(JSON.stringify(optionalFilters))}&sumOrFiltersScores=true&enableRules=false&enableReRanking=false`;
    }

    _userTokenParam() {
      const userToken = window.getAlgoliaUserToken?.();
      return userToken ? { userToken } : {};
    }

    async _fetch(requests) {
      const { appId, apiKey } = this._cfg;
      try {
        const res = await fetch(`https://${appId}-dsn.algolia.net/1/indexes/*/queries`, {
          method: 'POST',
          headers: {
            'X-Algolia-Application-Id': appId,
            'X-Algolia-API-Key': apiKey,
          },
          body: JSON.stringify({ requests }),
        });
        if (!res.ok) return requests.map(() => ({ hits: [] }));
        const { results } = await res.json();
        return results;
      } catch {
        return requests.map(() => ({ hits: [] }));
      }
    }

    async _loadTrending() {
      const { suggestions, products, trendingLimit } = this._cfg;
      const optionalFilters = this._buildOptionalFilters();
      const optionalFiltersParams = this._optionalFiltersParams(optionalFilters);
      const [trendingSearches, popularProducts] = await this._fetch([
        { indexName: suggestions, query: '', params: `hitsPerPage=${trendingLimit}`, ...this._userTokenParam() },
        { indexName: products,    query: '', params: `hitsPerPage=4&filters=NOT%20tags%3Ateam-logo%20AND%20NOT%20tags%3Ahide-product%20AND%20NOT%20tags%3Atapcart_exclusive${optionalFiltersParams}`, ...this._userTokenParam() },
      ]);

      this._fill('[data-trending-searches]',
        trendingSearches.hits.map(h =>
          `
          <li>
            <a data-suggestion href="/search?q=${encodeURIComponent(h.query)}">${formatProductTitle(h.query)}</a>
          </li>
          `
        ).join('')
      );

      this._fill('[data-popular-products]',
        popularProducts.hits.map(h => this._card(h)).join('')
      );
    }

    async _onInput(val) {
      if (!val) {
        this._showState('trending');
        return;
      }

      const { suggestions, products } = this._cfg;
      const optionalFilters = this._buildOptionalFilters();
      const optionalFiltersParams = this._optionalFiltersParams(optionalFilters);
      const [topSuggestions, productResults] = await this._fetch([
        { indexName: suggestions,  query: val, params: 'hitsPerPage=5', ...this._userTokenParam() },
        { indexName: products,     query: val, params: `hitsPerPage=9&clickAnalytics=true&filters=NOT%20tags%3Ateam-logo%20AND%20NOT%20tags%3Ahide-product%20AND%20NOT%20tags%3Atapcart_exclusive${optionalFiltersParams}`, ...this._userTokenParam() },
      ]);

      window.dispatchEvent(new CustomEvent('algolia:setData', {
        detail: { queryID: productResults.queryID, index: products }
      }));
      window.dispatchEvent(new CustomEvent('algolia:productsViewed', {
        detail: { products: productResults.hits }
      }));

      const suggestionHits = topSuggestions.hits;
      const { teams } = this._cfg;
      let teamHits = suggestionHits.map(() => null);
      if (teams) {
        const teamResults = await this._fetch(
          suggestionHits.map(h => ({
            indexName: teams,
            query: '',
            attributesToRetrieve: ['team_tag', 'name', 'logo', 'league', 'override'],
            params: `hitsPerPage=1&filters=${encodeURIComponent(`objectID:team_${h.query.toLowerCase().replaceAll(' ', '_')} AND published:true`)}`,
          }))
        );
        teamHits = teamResults.map(r => r.hits[0] ?? null);
      }

      this._fill('[data-top-suggestions]',
        suggestionHits.map((h, i) =>
          `
            <li>
              <a data-suggestion class="flex items-center" href="/search?q=${encodeURIComponent(h.query)}">
                ${teamHits[i] ? `<img src="${teamHits[i].logo}" alt="${teamHits[i].logo_alt || ''}" class="w-4 h-4 mr-2">` : ''}
                ${formatProductTitle(h.query, [teamHits[i]?.override])}
              </a>
            </li>
          `
        ).join('')
      );

      const hits = productResults.hits;
      this._fill('[data-top-products]',      hits.slice(0, 8).map(h => this._card(h)).join(''));
      this._showState('results');
    }

    _showState(state) {
      this.querySelectorAll('[data-state]').forEach(el =>
        el.classList.toggle('hidden', el.dataset.state !== state)
      );
      this.querySelector('[data-helpful-links]')?.classList.toggle('hidden', state === 'results');
    }

    _fill(selector, html) {
      const el = this.querySelector(selector);
      if (el) el.innerHTML = html;
    }

    _card(hit) {
      return `
        <li>
          <intersection-loader
            data-object-id="${hit.objectID}"
            data-position="${hit.position}"
            data-url="/products/${hit.handle}"
            data-section-id="product-card"
            data-extra="position:${hit.position},object-id:${hit.objectID}"
          >
            <div class="flex flex-col gap-3.5 w-full">
              <div class="bg-image-gray block relative aspect-173/216 overflow-hidden"></div>
              <div class="flex flex-col gap-2.5">
                <div class="flex flex-col gap-1.5">
                  <div class="h-4 w-full bg-image-gray"></div>
                  <div class="h-4 w-11 bg-image-gray"></div>
                </div>
                <div class="h-5 w-20 bg-image-gray"></div>
              </div>
            </div>
          </intersection-loader>
        </li>
      `;
    }
  });
}